import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../contexts/AuthContext';

// 작성자 이니셜 + 색상 (PageEditor 와 동일 톤)
const AV_COLORS = ['#2383e2', '#e0561d', '#0f9d58', '#a142f4', '#d93025', '#f4b400', '#1abc9c', '#9b59b6'];
function colorFor(id) {
  let h = 0;
  const s = String(id || '');
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AV_COLORS[h % AV_COLORS.length];
}

function timeAgo(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - t);
  const m = Math.floor(diff / 60000);
  if (m < 1) return '방금';
  if (m < 60) return `${m}분 전`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}시간 전`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}일 전`;
  return new Date(iso).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
}

function Avatar({ member, id }) {
  const name = member?.display_name || '?';
  const avatarUrl = member?.avatar_url;
  if (avatarUrl) {
    return <img className="cm-avatar" src={avatarUrl} alt={name} />;
  }
  return (
    <span className="cm-avatar" style={{ background: colorFor(id) }}>
      {name.slice(0, 1)}
    </span>
  );
}

// @멘션 본문을 일반 텍스트로 렌더(멘션 강조)
function renderBody(body) {
  const parts = String(body || '').split(/(@[^\s@]+)/g);
  return parts.map((p, i) => {
    if (p.startsWith('@')) {
      return (
        <span key={i} className="cm-mention">{p}</span>
      );
    }
    return <span key={i}>{p}</span>;
  });
}

export default function CommentsPanel({ pageId, onClose }) {
  const { user, membership } = useAuth();
  const orgId = membership?.org_id;

  const [comments, setComments] = useState([]);
  const [memberMap, setMemberMap] = useState({}); // user_id -> member
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState('');

  // 멤버 목록 로드(멘션 picker + 작성자 이름)
  useEffect(() => {
    let active = true;
    if (!orgId) return undefined;
    (async () => {
      const { data } = await supabase
        .schema('workspace').from('members')
        .select('user_id, display_name, avatar_url, org_id')
        .eq('org_id', orgId);
      if (!active) return;
      const list = data || [];
      setMembers(list);
      const map = {};
      list.forEach((m) => { map[m.user_id] = m; });
      setMemberMap(map);
    })();
    return () => { active = false; };
  }, [orgId]);

  const loadComments = useCallback(async () => {
    if (!pageId) return;
    setLoading(true);
    const { data, error } = await supabase
      .schema('workspace').from('comments')
      .select('id, page_id, block_id, author_id, body, resolved, parent_id, created_at')
      .eq('page_id', pageId)
      .order('created_at', { ascending: true });
    if (error) {
      setErrMsg('댓글을 불러오지 못했어요.');
      setComments([]);
    } else {
      setErrMsg('');
      setComments(data || []);
    }
    setLoading(false);
  }, [pageId]);

  useEffect(() => { loadComments(); }, [loadComments]);

  // 스레드 구성: top-level + 답글(1depth)
  const threads = useMemo(() => {
    const tops = [];
    const childrenOf = {};
    comments.forEach((c) => {
      if (c.parent_id) {
        (childrenOf[c.parent_id] = childrenOf[c.parent_id] || []).push(c);
      } else {
        tops.push(c);
      }
    });
    return tops.map((t) => ({ ...t, replies: childrenOf[t.id] || [] }));
  }, [comments]);

  async function toggleResolve(comment) {
    const next = !comment.resolved;
    // 낙관적 업데이트
    setComments((prev) => prev.map((c) => (c.id === comment.id ? { ...c, resolved: next } : c)));
    const { error } = await supabase
      .schema('workspace').from('comments')
      .update({ resolved: next })
      .eq('id', comment.id);
    if (error) {
      // 롤백
      setComments((prev) => prev.map((c) => (c.id === comment.id ? { ...c, resolved: comment.resolved } : c)));
      setErrMsg('해결 상태 변경에 실패했어요.');
    }
  }

  return (
    <aside className="cm-panel" role="complementary" aria-label="댓글">
      <header className="cm-head">
        <span className="cm-head-title">댓글</span>
        <button type="button" className="cm-close" onClick={onClose} aria-label="댓글 닫기">✕</button>
      </header>

      <div className="cm-list">
        {loading ? (
          <div className="cm-state"><div className="spinner" /></div>
        ) : errMsg ? (
          <div className="cm-state cm-state-err">{errMsg}</div>
        ) : threads.length === 0 ? (
          <div className="cm-state">
            <div className="cm-state-big">💬</div>
            <div>아직 댓글이 없어요.</div>
            <div className="muted" style={{ fontSize: 12 }}>첫 댓글을 남겨보세요. @로 동료를 멘션할 수 있어요.</div>
          </div>
        ) : (
          threads.map((t) => (
            <CommentItem
              key={t.id}
              comment={t}
              replies={t.replies}
              memberMap={memberMap}
              members={members}
              currentUserId={user?.id}
              pageId={pageId}
              onToggleResolve={toggleResolve}
              onReplied={loadComments}
              onError={setErrMsg}
            />
          ))
        )}
      </div>

      <CommentComposer
        pageId={pageId}
        parentId={null}
        members={members}
        placeholder="댓글 추가…"
        submitLabel="댓글"
        onPosted={loadComments}
        onError={setErrMsg}
      />
    </aside>
  );
}

function CommentItem({
  comment, replies, memberMap, members, currentUserId, pageId,
  onToggleResolve, onReplied, onError,
}) {
  const [replying, setReplying] = useState(false);
  const author = memberMap[comment.author_id];
  const authorName = author?.display_name || '알 수 없는 사용자';

  return (
    <div className={`cm-thread${comment.resolved ? ' is-resolved' : ''}`}>
      <div className="cm-item">
        <Avatar member={author} id={comment.author_id} />
        <div className="cm-body">
          <div className="cm-meta">
            <span className="cm-name">{authorName}</span>
            <span className="cm-time">{timeAgo(comment.created_at)}</span>
            {comment.resolved ? <span className="cm-badge">해결됨</span> : null}
          </div>
          <div className="cm-text">{renderBody(comment.body)}</div>
          <div className="cm-actions">
            <button type="button" className="cm-action" onClick={() => setReplying((r) => !r)}>
              답글
            </button>
            <button type="button" className="cm-action" onClick={() => onToggleResolve(comment)}>
              {comment.resolved ? '다시 열기' : '해결'}
            </button>
          </div>
        </div>
      </div>

      {replies.length > 0 ? (
        <div className="cm-replies">
          {replies.map((r) => {
            const ra = memberMap[r.author_id];
            return (
              <div className="cm-item cm-reply" key={r.id}>
                <Avatar member={ra} id={r.author_id} />
                <div className="cm-body">
                  <div className="cm-meta">
                    <span className="cm-name">{ra?.display_name || '알 수 없는 사용자'}</span>
                    <span className="cm-time">{timeAgo(r.created_at)}</span>
                  </div>
                  <div className="cm-text">{renderBody(r.body)}</div>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      {replying ? (
        <div className="cm-reply-composer">
          <CommentComposer
            pageId={pageId}
            parentId={comment.id}
            members={members}
            placeholder="답글 추가…"
            submitLabel="답글"
            compact
            onPosted={() => { setReplying(false); onReplied(); }}
            onError={onError}
          />
        </div>
      ) : null}
    </div>
  );
}

function CommentComposer({
  pageId, parentId, members, placeholder, submitLabel, compact,
  onPosted, onError,
}) {
  const [text, setText] = useState('');
  const [mentions, setMentions] = useState([]); // collected user_ids
  const [posting, setPosting] = useState(false);
  const [picker, setPicker] = useState(null); // { query, start } when '@' active
  const taRef = useRef(null);

  const filtered = useMemo(() => {
    if (!picker) return [];
    const q = picker.query.toLowerCase();
    return members
      .filter((m) => (m.display_name || '').toLowerCase().includes(q))
      .slice(0, 6);
  }, [picker, members]);

  function detectMention(value, caret) {
    // caret 직전의 '@단어' 패턴 탐지
    const upto = value.slice(0, caret);
    const m = upto.match(/@([^\s@]*)$/);
    if (m) {
      setPicker({ query: m[1], start: caret - m[0].length });
    } else {
      setPicker(null);
    }
  }

  function onChange(e) {
    const v = e.target.value;
    setText(v);
    detectMention(v, e.target.selectionStart);
  }

  function pickMember(member) {
    if (!picker) return;
    const before = text.slice(0, picker.start);
    const after = text.slice((taRef.current && taRef.current.selectionStart) || picker.start);
    const inserted = `@${member.display_name} `;
    const next = before + inserted + after;
    setText(next);
    setMentions((prev) => (prev.includes(member.user_id) ? prev : [...prev, member.user_id]));
    setPicker(null);
    // 커서를 삽입 위치 뒤로
    requestAnimationFrame(() => {
      if (taRef.current) {
        const pos = (before + inserted).length;
        taRef.current.focus();
        taRef.current.setSelectionRange(pos, pos);
      }
    });
  }

  async function submit() {
    const body = text.trim();
    if (!body || posting) return;
    setPosting(true);
    // 본문에 실제 남아있는 멘션만 전달
    const activeMentions = mentions.filter((uid) => {
      const m = members.find((x) => x.user_id === uid);
      if (!m || !m.display_name) return false;
      // 이름이 다른 이름의 부분집합일 때 오탐을 막기 위해 토큰 경계(@이름 뒤 공백/끝)까지 확인
      const esc = m.display_name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return new RegExp(`@${esc}(\\s|$)`).test(text);
    });
    const { data, error } = await supabase.rpc('post_comment', {
      p_page: pageId,
      p_body: body,
      p_parent: parentId,
      p_mentions: activeMentions,
    });
    setPosting(false);
    if (error || !(data && data.ok)) {
      onError && onError('댓글 등록에 실패했어요.');
      return;
    }
    setText('');
    setMentions([]);
    setPicker(null);
    onPosted && onPosted();
  }

  function onKeyDown(e) {
    // 멘션 picker 가 열려있고 Enter 면 첫 후보 선택
    if (picker && filtered.length > 0 && e.key === 'Enter') {
      e.preventDefault();
      pickMember(filtered[0]);
      return;
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      submit();
    }
  }

  return (
    <div className={`cm-composer${compact ? ' is-compact' : ''}`}>
      <div className="cm-composer-box">
        <textarea
          ref={taRef}
          className="cm-input"
          value={text}
          onChange={onChange}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          rows={compact ? 2 : 3}
          spellCheck={false}
        />
        {picker && filtered.length > 0 ? (
          <div className="cm-mention-list" role="listbox">
            {filtered.map((m) => (
              <button
                key={m.user_id}
                type="button"
                className="cm-mention-opt"
                onMouseDown={(e) => { e.preventDefault(); pickMember(m); }}
              >
                <Avatar member={m} id={m.user_id} />
                <span>{m.display_name}</span>
              </button>
            ))}
          </div>
        ) : null}
      </div>
      <div className="cm-composer-foot">
        <span className="cm-hint muted">@로 멘션 · ⌘/Ctrl+Enter 전송</span>
        <button
          type="button"
          className="cm-submit"
          onClick={submit}
          disabled={posting || !text.trim()}
        >
          {posting ? '등록 중…' : submitLabel}
        </button>
      </div>
    </div>
  );
}
